import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useState } from "react";
import {
	Card, type SelectOption, Select, type SelectComponent, SettingsLayout, Switch
} from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import type { SetSystemSecurityPayload, SystemSecuritySettingsState } from "Interfaces";
import {
	selectSystemSecuritySettings, setSystemSecuritySettingsAction
} from "Data/Redux/Slices/Settings/System/Security";
import { produce } from "immer";
import { isEqual, merge } from "lodash";
import "Components/ConfigLayout/System/SecuritySettings/SecuritySettings.less";

const durationOptions: SelectOption[] =
	[ 1, 2, 3, 4, 5, 10, 15, 20, 30, 60, 120 ].map(duration => ({
		label: `${ duration } Min`,
		value: duration
	}));

type SecuritySettingsForm = {
	autoLogoutSelect: SelectComponent;
	autoLogoutDurationSelect: SelectComponent;
} & HTMLFormElement;

const SecuritySettings = () => {
	const dispatch = useAppDispatch();
	const securitySettings = useAppSelector(selectSystemSecuritySettings);
	const [ securitySettingsDiff, setSecuritySettingsDiff ] = useState<Partial<SystemSecuritySettingsState>>({});
	const setSystemSecuritySettings = (newSettings: SetSystemSecurityPayload) =>
		dispatch(setSystemSecuritySettingsAction(newSettings));

	const localSecuritySettings = produce(securitySettings, (draft) => {
		merge(draft, securitySettingsDiff);
	});

	const handleSubmit = (event: ReactSubmitEvent<SecuritySettingsForm>) => {
		event.preventDefault();

		setSystemSecuritySettings(localSecuritySettings);
	};

	const handleReset = (event: FormEvent<SecuritySettingsForm>) => {
		event.preventDefault();

		setSecuritySettingsDiff({});
	};

	const handleAutoLogoutDurationSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		const newDiffState = produce(localSecuritySettings, draft => {
			draft.autoLogoutDuration = +value;
		});

		setSecuritySettingsDiff(newDiffState);
	};

	const handleAutoLogoutEnabledChange = (checked: boolean) => {
		const newDiffState = produce(securitySettingsDiff, draft => {
			draft.autoLogout = checked;
		});

		setSecuritySettingsDiff(newDiffState);
	};

	return (
		<SettingsLayout.Form
			className="security-settings-content"
			noValidate
			onReset={ handleReset }
			onSubmit={ handleSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="small" title="Security">
					<div className="form-row switch">
						<span>Auto Logout</span>
						<Switch
							formName="autoLogoutSelect"
							onChange={ handleAutoLogoutEnabledChange }
							checked={ localSecuritySettings.autoLogout }
						/>
					</div>
					<div className="form-row">
						<span>Auto Logout Duration</span>
						<Select
							allowClear={ false }
							className="form-select"
							disabled={ !localSecuritySettings.autoLogout }
							name="autoLogoutDurationSelect"
							onSelect={ handleAutoLogoutDurationSelect }
							options={ durationOptions }
							value={ localSecuritySettings.autoLogoutDuration }
						/>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(localSecuritySettings, securitySettings) } />
		</SettingsLayout.Form>
	);
};

export default memo(SecuritySettings);
